"use client";

import { useDeferredValue, useState } from "react";

import type { TaggedRecipe } from "../../_types/content";
import RecipeLinkCard from "./RecipeLinkCard";
import CatalogTemplate from "./ui/CatalogTemplate";
import { Select } from "./ui/Primitives";
import { controlStyles, layoutStyles } from "./ui/style-config";

type RecipeSort = "featured" | "title" | "time-ascending" | "time-descending";

export interface RecipeCatalogFilters {
  grainTags: string[];
  difficulty: TaggedRecipe["difficulty"] | "all";
  sort: RecipeSort;
}

const defaultFilters: RecipeCatalogFilters = {
  grainTags: [],
  difficulty: "all",
  sort: "featured",
};

export function filterAndSortRecipes(
  recipes: readonly TaggedRecipe[],
  filters: RecipeCatalogFilters,
) {
  return [...recipes]
    .filter(
      (recipe) =>
        filters.grainTags.length === 0 ||
        filters.grainTags.some((grain) => recipe.tagLabels.includes(grain)),
    )
    .filter(
      (recipe) =>
        filters.difficulty === "all" ||
        recipe.difficulty === filters.difficulty,
    )
    .toSorted((first, second) => {
      const firstTime = first.prepMinutes + first.cookMinutes;
      const secondTime = second.prepMinutes + second.cookMinutes;

      switch (filters.sort) {
        case "title":
          return first.title.localeCompare(second.title);
        case "time-ascending":
          return firstTime - secondTime;
        case "time-descending":
          return secondTime - firstTime;
        default:
          if (first.featured !== second.featured) {
            return first.featured ? -1 : 1;
          }

          return (
            (first.featuredOrder ?? Number.MAX_SAFE_INTEGER) -
            (second.featuredOrder ?? Number.MAX_SAFE_INTEGER)
          );
      }
    });
}

function RecipeFilterControls({
  filters,
  setFilters,
  grainTags,
}: {
  filters: RecipeCatalogFilters;
  setFilters: (filters: RecipeCatalogFilters) => void;
  grainTags: string[];
}) {
  function toggleGrain(grain: string) {
    setFilters({
      ...filters,
      grainTags: filters.grainTags.includes(grain)
        ? filters.grainTags.filter((value) => value !== grain)
        : [...filters.grainTags, grain],
    });
  }

  return (
    <div className="space-y-7">
      <fieldset>
        <legend className="font-neueheitz text-2xl">Grain</legend>
        <div className={layoutStyles.filterOptions}>
          {grainTags.map((grain) => (
            <label key={grain} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.grainTags.includes(grain)}
                onChange={() => toggleGrain(grain)}
                className={controlStyles.checkbox}
              />
              {grain}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="block font-[700]">
        Difficulty
        <Select
          value={filters.difficulty}
          onChange={(event) =>
            setFilters({
              ...filters,
              difficulty: event.target.value as RecipeCatalogFilters["difficulty"],
            })
          }
        >
          <option value="all">All levels</option>
          <option value="easy">Easy</option>
          <option value="moderate">Moderate</option>
          <option value="advanced">Advanced</option>
        </Select>
      </label>

      <label className="block font-[700]">
        Sort by
        <Select
          value={filters.sort}
          onChange={(event) =>
            setFilters({ ...filters, sort: event.target.value as RecipeSort })
          }
        >
          <option value="featured">Featured</option>
          <option value="title">Title: A-Z</option>
          <option value="time-ascending">Time: Shortest first</option>
          <option value="time-descending">Time: Longest first</option>
        </Select>
      </label>

      <button
        type="button"
        onClick={() => setFilters(defaultFilters)}
        className="border-b-2 border-burgundy font-[700]"
      >
        Reset filters
      </button>
    </div>
  );
}

export default function RecipeCatalog({
  recipes,
}: {
  recipes: readonly TaggedRecipe[];
}) {
  const [filters, setFilters] = useState(defaultFilters);
  const deferredFilters = useDeferredValue(filters);
  const filteredRecipes = filterAndSortRecipes(recipes, deferredFilters);
  const grainTags = [...new Set(recipes.flatMap(({ tagLabels }) => tagLabels))]
    .toSorted();
  const filterProps = { filters, setFilters, grainTags };

  return (
    <CatalogTemplate
      filters={<RecipeFilterControls {...filterProps} />}
      count={filteredRecipes.length}
      countLabel="recipes"
      isEmpty={filteredRecipes.length === 0}
      emptyMessage="No recipes match these filters."
      onReset={() => setFilters(defaultFilters)}
    >
      {filteredRecipes.map((recipe) => (
        <RecipeLinkCard key={recipe.id} recipe={recipe} />
      ))}
    </CatalogTemplate>
  );
}
